import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { NavNextIcon, NavPrevIcon } from "../../primitives/Icons/Icons";
import activeSlideIndex from "./activeSlideIndex";
import css from "./Carousel.module.css";

const CarouselContext = React.createContext({
  trackRef: { current: null },
  activeIndex: 0,
  slideCount: 0,
  scrollToSlide: () => {},
  setActiveIndex: () => {},
  setSlideCount: () => {},
  loop: false,
});

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CarouselRoot = React.forwardRef(
  (
    {
      slidesPerView = 1,
      gap = "0px",
      loop = false,
      label,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const trackRef = React.useRef(null);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [slideCount, setSlideCount] = React.useState(0);

    const scrollToSlide = React.useCallback((index) => {
      const track = trackRef.current;
      if (!track) return;
      const slides = track.children;
      if (!slides[index]) return;
      const target = slides[index];
      track.scrollTo({
        left: target.offsetLeft,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    }, []);

    const slideWidth =
      slidesPerView === "auto"
        ? "auto"
        : `calc((100% - (${gap} * ${slidesPerView - 1})) / ${slidesPerView})`;

    const ctx = React.useMemo(
      () => ({
        trackRef,
        activeIndex,
        slideCount,
        scrollToSlide,
        setActiveIndex,
        setSlideCount,
        loop,
      }),
      [activeIndex, slideCount, scrollToSlide, loop],
    );

    return (
      <CarouselContext.Provider value={ctx}>
        <div
          ref={ref}
          role="region"
          aria-roledescription="carousel"
          aria-label={label}
          className={clsx(css.root, className)}
          style={{
            "--carousel-gap": gap,
            "--carousel-slide-width": slideWidth,
          }}
          {...rest}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);

CarouselRoot.displayName = "CarouselRoot";

CarouselRoot.propTypes = {
  slidesPerView: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf(["auto"]),
  ]),
  gap: PropTypes.string,
  loop: PropTypes.bool,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

const CarouselTrack = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const { trackRef, setActiveIndex, setSlideCount } =
      React.useContext(CarouselContext);

    const mergedRef = React.useCallback(
      (node) => {
        trackRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      },
      [ref, trackRef],
    );

    React.useEffect(() => {
      const track = trackRef.current;
      if (!track) return;

      const slides = Array.from(track.children);
      setSlideCount(slides.length);

      const observer = new IntersectionObserver(
        (entries) => {
          const index = activeSlideIndex(slides)(entries);
          if (index !== undefined) setActiveIndex(index);
        },
        { root: track, threshold: 0.5 },
      );

      slides.forEach((slide) => observer.observe(slide));

      return () => observer.disconnect();
    }, [trackRef, setActiveIndex, setSlideCount, children]);

    return (
      <div ref={mergedRef} className={clsx(css.track, className)} {...rest}>
        {children}
      </div>
    );
  },
);

CarouselTrack.displayName = "CarouselTrack";

CarouselTrack.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const CarouselSlide = React.forwardRef(
  ({ index = 0, className, children, ...rest }, ref) => (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      aria-label={`Slide ${index + 1}`}
      className={clsx(css.slide, className)}
      {...rest}
    >
      {children}
    </div>
  ),
);

CarouselSlide.displayName = "CarouselSlide";

CarouselSlide.propTypes = {
  index: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
};

const CarouselPrev = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const { activeIndex, scrollToSlide, slideCount, loop } =
      React.useContext(CarouselContext);
    const disabled = !loop && activeIndex === 0;

    const handleClick = () => {
      const target = activeIndex > 0 ? activeIndex - 1 : slideCount - 1;
      scrollToSlide(target);
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Previous slide"
        className={clsx(css.navBtn, css.prev, className)}
        disabled={disabled}
        onClick={handleClick}
        {...rest}
      >
        {children || <NavPrevIcon />}
      </button>
    );
  },
);

CarouselPrev.displayName = "CarouselPrev";

CarouselPrev.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const CarouselNext = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const { activeIndex, scrollToSlide, slideCount, loop } =
      React.useContext(CarouselContext);
    const disabled = !loop && activeIndex >= slideCount - 1;

    const handleClick = () => {
      const target = activeIndex < slideCount - 1 ? activeIndex + 1 : 0;
      scrollToSlide(target);
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Next slide"
        className={clsx(css.navBtn, css.next, className)}
        disabled={disabled}
        onClick={handleClick}
        {...rest}
      >
        {children || <NavNextIcon />}
      </button>
    );
  },
);

CarouselNext.displayName = "CarouselNext";

CarouselNext.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const CarouselDots = React.forwardRef(({ className, ...rest }, ref) => {
  const { activeIndex, slideCount, scrollToSlide } =
    React.useContext(CarouselContext);

  return (
    <div
      ref={ref}
      role="tablist"
      className={clsx(css.dots, className)}
      {...rest}
    >
      {Array.from({ length: slideCount }, (_, i) => (
        <button
          key={i}
          type="button"
          role="tab"
          aria-selected={i === activeIndex}
          aria-label={`Go to slide ${i + 1}`}
          className={clsx(css.dot, i === activeIndex && css.dotActive)}
          onClick={() => scrollToSlide(i)}
        />
      ))}
    </div>
  );
});

CarouselDots.displayName = "CarouselDots";

CarouselDots.propTypes = {
  className: PropTypes.string,
};

const CarouselCounter = React.forwardRef(({ className, ...rest }, ref) => {
  const { activeIndex, slideCount } = React.useContext(CarouselContext);

  return (
    <span
      ref={ref}
      aria-live="polite"
      aria-atomic="true"
      className={clsx(css.counter, className)}
      {...rest}
    >
      {activeIndex + 1} / {slideCount}
    </span>
  );
});

CarouselCounter.displayName = "CarouselCounter";

CarouselCounter.propTypes = {
  className: PropTypes.string,
};

const Carousel = {
  Root: CarouselRoot,
  Track: CarouselTrack,
  Slide: CarouselSlide,
  Prev: CarouselPrev,
  Next: CarouselNext,
  Dots: CarouselDots,
  Counter: CarouselCounter,
};

export default Carousel;
